import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { CompactFormToolbar } from "../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../common/form/CompactSaveButton";
import { useCallback } from "react";
import { useAuthProvider, useNotify, useTranslate } from "react-admin";
import { AuthManager } from "../../common/auth/Auth";
import { handleAxiosError } from "../../common/data/axiosErrorHandler";
import { Box, Card, CardContent, Theme, Typography } from "@material-ui/core";
import classnames from "classnames";
import { HookFormTenantPicker } from "../common/form/hook-form/inputs/HookFormTenantPicker";
import { Info } from "@material-ui/icons";
import makeStyles from "@material-ui/styles/makeStyles";

const useStyles = makeStyles((theme: Theme) => ({
  info: {
    color: theme.palette.info.main,
    marginRight: "5px",
  },
}));

interface Props {
  isOverridden: boolean;
  onSuccess: () => void;
  onFailed?: () => void;
}

const DefaultCompactFormToolbar = () => {
  const {
    formState: { isSubmitting, isValid },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton
        label="Switch"
        saving={isSubmitting}
        disabled={isSubmitting || !isValid}
      />
    </CompactFormToolbar>
  );
};

type FormValues = {
  id: string;
};

export const SwitchToCustomerForm = ({
  onSuccess,
  onFailed,
  isOverridden,
  ...rest
}: Props) => {
  const classes = useStyles();
  const authManager = useAuthProvider() as AuthManager;
  const notify = useNotify();
  const translate = useTranslate();

  const onSubmit = useCallback(
    (data: FormValues) => {
      return authManager
        .setUserOverride(isOverridden ? undefined : { id: data.id })
        .then(() => {
          onSuccess();
        })
        .catch((e) => {
          if (onFailed) {
            onFailed();
          }
          console.error(e);
          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [authManager, onSuccess, onFailed, notify, isOverridden]
  );

  const formContext = useForm<FormValues>({
    mode: "onChange",
  });

  const { handleSubmit, control } = formContext;

  const handleSubmitInternal: SubmitHandler<FormValues> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", {
        type: "error",
      });
    });
  };

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}
        >
          <Card>
            <CardContent>
              {!isOverridden ? (
                <div className="ra-input">
                  <HookFormTenantPicker<FormValues>
                    control={control}
                    name="id"
                    label="Customer"
                    rules={{ required: translate("ra.validation.required") }}
                    variant="outlined"
                    defaultValue={null}
                  />
                </div>
              ) : (
                <Box display="flex">
                  <Info className={classes.info} />
                  <Typography variant="body1">
                    Confirm switching back to master admin interface
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar />
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
